import React, { useEffect, useState } from 'react'
import AreaCodeFilter from './search-filters/area-code-filter/AreaCodeFilter'
import NumberTypeFilter, { NumberTypeMap } from './search-filters/number-type-filter/NumberTypeFilter'
import KeywordFilter from './search-filters/keyword-filter/KeywordFilter'
import { makeStyles } from '@material-ui/core/styles'
import { Typography, Button } from 'foundations-library/components'
import * as API from './API'

const areaCodeSelectTestId = 'area-code-filter-select'
const areaCodeAutoCompleteTestId = 'area-code-filter-auto-complete'
const keywordFilterTestId = 'keyword-search'

const useStyles = makeStyles(theme => {
    const { colors } = theme['foundations-library']

    return {
        header: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flexGrow: 0
        },
        grid: {
            maxWidth: '464px',
            width: '100%'
        },
        numberTypeFilter: {
            position: 'absolute',
            zIndex: 1111,
            top: '50px',
            left: '50%',
            transform: 'translate(-50%, -50%)'
        },
        inputsContainer: {
            margin: ({ numberTypesEnabled, showTypeSelector }) => !numberTypesEnabled.toll_free ? `${showTypeSelector ? '120px' : '10px'} auto 0 auto` : '0 auto',
            display: 'flex',
            height: '50px',
            padding: '3px 3px 3px 30px',
            alignItems: 'center',
            gap: '40px',
            alignSelf: 'stretch',
            borderRadius: '30px',
            border: `1px solid ${colors.neutral[100]}`,
            background: `${colors.neutral[50]}`,
            [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
                gap: '5px',
                padding: 0,
                height: 'auto',
                paddingBottom: '6px'
            }
        },
        inputs: {
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            flex: '1 0 0',
            [theme.breakpoints.down('sm')]: {
                gap: '10px'
            },
            '& .area-code-autocomplete': {
                [theme.breakpoints.down('sm')]: {
                    flexBasis: 0,
                    flexGrow: 1
                },
                '& .text-field-mui': {
                    width: '150px',
                    '& .Mui-focused': {
                        backgroundColor: 'transparent !important'
                    },
                    [theme.breakpoints.down('sm')]: {
                        width: '100%'
                    }
                },
                '& .text-field-mui .input-root:hover': {
                    backgroundColor: 'transparent',
                    borderBottom: 'none'
                },
                '& .text-field-mui .input-root:hover::before, & .text-field-mui .input-root:hover::after': {
                    borderColor: 'transparent !important'
                },
                '& .text-field-mui .input-root::after': {
                    borderBottom: 'none'
                },
                '& .text-field-mui .input-root .adorned-end-x svg': {
                    color: '#f00 !important'
                },
                '& .text-field-mui .input-root .caret > span': {
                    color: '#000 !important'
                }
            },
            '& .select-form-control': {
                minWidth: 'initial',
                [`& [data-testid="${areaCodeSelectTestId}"]`]: {
                    width: '150px !important',
                    '&.Mui-focused': {
                        backgroundColor: 'transparent !important'
                    }
                },
                [`& [data-testid="${areaCodeSelectTestId}"]:hover`]: {
                    backgroundColor: 'transparent !important'
                },
                [`& [data-testid="${areaCodeSelectTestId}"]::after`]: {
                    borderBottom: 'none !important'
                },
                [`& [data-testid="${areaCodeSelectTestId}"]:hover::before`]: {
                    borderColor: 'transparent !important'
                }
            },
            '& .keyword-text-field-root': {
                flexGrow: 1,
                [theme.breakpoints.down('sm')]: {
                    flexBasis: 0
                },
                '& .MuiFilledInput-underline': {
                    backgroundColor: 'transparent !important'
                },
                '& .MuiFilledInput-underline:hover::before, & .MuiFilledInput-underline:hover::after': {
                    borderColor: 'transparent !important'
                },
                '& .MuiFilledInput-underline::after': {
                    borderBottom: 'none !important'
                },
                '& .MuiFilledInput-adornedEnd .adorned-end-x': {
                    [theme.breakpoints.down('sm')]: {
                        marginRight: 0
                    },
                    '& svg': {
                        color: 'rgba(0, 0, 0, 0.54) !important'
                    }
                }
            }
        },
        andOrText: {
            color: colors.neutral[900]
        },
        findYourNumberButton: {
            height: '100% !important',
            border: 0,
            borderRadius: '25px !important',
            cursor: 'pointer'
        },
        oneTimeFeeText: {
            margin: ({ showTypeSelector }) => showTypeSelector ? '100px 0 30px 0' : '10px 0 30px 0'
        }
    }
})

/**
 * Configuration applicable to all types of filters
 */
export interface FilterOptions {
    name: 'AreaCode' | 'NumberType' | 'KeywordSearch'
    size: number
}

/**
 * Represents a given set of values that can be used to
 * perform a number picker search. This format is independent of the
 * filterBy / request payload used by the number search APIs.
 */
export interface FilterValues {
    numberTypesEnabled: NumberTypeMap;
    selectedAreaCode: string | null;
    keyword: string;
}

interface HeaderProps {
    initData: API.InitResponseData;

    initialNumberType: string | null;
    initialAreaCode: string | null;
    initialKeyword: string;

    filters: FilterOptions[];
    showResetButton?: boolean;
    fetchNumbers: (filterValues: FilterValues) => void;
    isSmallView: boolean;

    disabled: boolean;
    loading: boolean;
}

/**
 * The header to the number picker that contains a row of search filters,
 * and a search bar displayed below the search filters.
 */
const Header = ({ initData, initialNumberType, initialAreaCode, initialKeyword, filters, fetchNumbers, isSmallView, disabled, loading }: HeaderProps): JSX.Element => {
    const showTypeSelector = filters.some(filter => filter.name === 'NumberType')
    const showAreaCodeSelector = filters.some(filter => filter.name === 'AreaCode')
    const showKeywordSelector = filters.some(filter => filter.name === 'KeywordSearch')

    const [numberTypesEnabled, setNumberTypesEnabled] = useState<NumberTypeMap>({
        local: initialNumberType === 'local',
        toll_free: initialNumberType === 'toll-free' // eslint-disable-line @typescript-eslint/naming-convention
    })
    const classes = useStyles({ isSmallView, numberTypesEnabled, showTypeSelector })

    const localAreaCodes = initData.npas.local
    const tollFreeAreaCodes = initData.npas.toll_free

    const [selectedAreaCode, setSelectedAreaCode] = useState<string | null>(initialAreaCode)

    const [keyword, setKeyword] = useState<string>(initialKeyword)

    // Note: in theory both local and toll free can be selected,
    // populate the area code filter with the set of area codes that makes sense based on
    // all selected types of numbers.
    let areaCodeOptions: string[] = []
    if (numberTypesEnabled.toll_free) {
        areaCodeOptions = areaCodeOptions.concat(tollFreeAreaCodes)
    }
    if (numberTypesEnabled.local) {
        areaCodeOptions = areaCodeOptions.concat(localAreaCodes)
    }

    // Logic to make sure selectedAreaCode and number type agree
    useEffect(() => {
        if (localAreaCodes.length > 0 && tollFreeAreaCodes.length > 0 && selectedAreaCode) {
            const isValidTollFreeCode = tollFreeAreaCodes.includes(selectedAreaCode)
            const isValidLocalCode = localAreaCodes.includes(selectedAreaCode)

            if (numberTypesEnabled.toll_free) {
                if (!isValidTollFreeCode && isValidLocalCode) {
                    console.warn('pdcNumberPicker: switching to local as the selected area code is not a toll free code, but is a local area code')
                    setNumberTypesEnabled({ toll_free: false, local: true }) // eslint-disable-line @typescript-eslint/naming-convention
                } else if (!isValidTollFreeCode && selectedAreaCode) {
                    console.warn('pdcNumberPicker: clearing selected area code which is neither a valid local area code nor a toll-free code')
                    setSelectedAreaCode(null)
                }
            } else if (numberTypesEnabled.local) {
                if (!isValidLocalCode && isValidTollFreeCode) {
                    console.warn('pdcNumberPicker: switching to toll-free as the selected area code is not a local code, but is a tol-free area code')
                    setNumberTypesEnabled({ local: false, toll_free: true }) // eslint-disable-line @typescript-eslint/naming-convention
                } else if (!isValidLocalCode && selectedAreaCode) {
                    console.warn('pdcNumberPicker: clearing selected area code which is neither a valid local area code nor a toll-free code')
                    setSelectedAreaCode(null)
                }
            }
        }
    }, [selectedAreaCode, localAreaCodes, tollFreeAreaCodes, setNumberTypesEnabled])

    const handleFindMyNumber = (e) => {
        e && e.preventDefault()
        fetchNumbers({
            numberTypesEnabled,
            selectedAreaCode,
            keyword
        })
    }

    useEffect(() => {
        fetchNumbers({
            numberTypesEnabled,
            selectedAreaCode,
            keyword
        })
    }, [])

    return (
        <form onSubmit={handleFindMyNumber} className={classes.header}>
            {showTypeSelector && <NumberTypeFilter
                numberTypesEnabled={numberTypesEnabled}
                onChange={numberTypeMap => {
                    setSelectedAreaCode(null)
                    setNumberTypesEnabled(numberTypeMap)
                    fetchNumbers({
                        numberTypesEnabled: numberTypeMap,
                        selectedAreaCode: null,
                        keyword
                    })
                }}
                disabled={disabled}
                className={classes.numberTypeFilter}
            />}
            {numberTypesEnabled.toll_free && <Typography variant="body2" color="neutral-700" className={classes.oneTimeFeeText}>
                All 800 numbers have a $15+ one-time fee and all 888 numbers have a $10+ one-time fee.
                Other toll free numbers may have a one-time fee of up to $49.
            </Typography>}
            {(showAreaCodeSelector || showKeywordSelector) && <div className={classes.inputsContainer}>
                <div className={classes.inputs}>
                    {showAreaCodeSelector && <AreaCodeFilter
                        areaCodeOptions={areaCodeOptions}
                        numberTypesEnabled={numberTypesEnabled}
                        setNumberTypesEnabled={setNumberTypesEnabled}
                        selectedAreaCode={selectedAreaCode}
                        onChange={areaCode => setSelectedAreaCode(areaCode)}
                        disabled={disabled}
                        dataTestId={{ select: areaCodeSelectTestId, autoComplete: areaCodeAutoCompleteTestId }}
                    />}
                    {showAreaCodeSelector && showKeywordSelector && <Typography variant="body2" className={classes.andOrText}>and/or</Typography>}
                    {showKeywordSelector && <KeywordFilter
                        initialKeyword={initialKeyword}
                        onChange={newKeyword => setKeyword(newKeyword)}
                        disabled={disabled}
                        dataTestId={keywordFilterTestId}
                    />}
                </div>
                <Button
                    disabled={disabled || loading}
                    className={classes.findYourNumberButton}
                    variant="contained"
                    color="primary"
                    size="large"
                    icon={'go'}
                    type='submit'
                >Find your number</Button>
            </div>}
        </form>
    )
}

export default Header
